/**
 * May this session go here, and what does the map look like afterwards?
 *
 * One decision with two halves that must not drift: whether a drop is allowed, and what it writes.
 * A component that judged the target and then wrote the override itself would be two rules with one
 * test between them — and the half without the test is the one that sends a coordinate the backend
 * 400s.
 *
 * ── A refusal is WORDED, because this drag has a keyboard path ───────────────────────────────
 * View ADR §11 lets a pointer drag refuse silently: the target is simply never entered, no
 * indicator paints, nothing happens. That is the right answer for a pointer and the wrong one for
 * the arrows — a keyboard trainer aiming at a cell has asked the question explicitly and is owed
 * the answer. So the verdict is a VALUE, the pointer path uses it to refuse the target and the
 * keyboard path announces it.
 *
 * ── A session never leaves its own cycle ─────────────────────────────────────────────────────
 * Owner decision, 22 September 2026. A cycle IS a calendar week here, and the rail names it `T1`,
 * so a session dragged into the row below is a session that has changed which cycle it belongs to
 * while keeping the coordinate that says otherwise — the chip would sit under `T2` carrying cycle
 * 1's coordinate, and the trainer would be reading a calendar whose rail lies. The move a trainer
 * actually wants there is a different weekday, and that is the only one offered.
 *
 * The bound is the PATTERN week, never the week the chip currently sits in: taken from where it
 * stands, each move would re-anchor the next one and a chip could walk the whole plan a week at a
 * time. A withheld cycle-1 session is the same rule — its pattern week is week one — and the
 * `beforeStart` floor is what keeps the rescue inside the part of that week the client can train.
 *
 * ── Dropping on the pattern day RESTORES, and never writes an identity override ──────────────
 * An override whose date equals the generated date is a no-op the backend still stores, and it
 * would make the chip read „przeniesiona z pon., 28 wrz" while sitting on Monday 28 September.
 * Dragging a moved session back where it came from is the trainer undoing the move, so the
 * override is deleted.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. Verdicts leave as data.
 */

import { addDays, atMidnight, mondayOf, parseIsoDate } from '~/shared/lib/calendarDate';

import { coordinateKey } from './assignSchedule';
import type { DateWindow, OccurrenceOverride } from './assignRequest';
import type { ProjectionChip } from './projectionGrid';

/**
 * Why a drop cannot happen, or `null` when it can.
 *
 * `beforeStart` is a refusal here although the SERVER would accept the request and simply withhold
 * the occurrence (`ScheduleGenerator` line 197). Accepting it would mean the trainer drags a
 * session onto a date, watches it go dashed, and has silently removed it from the plan — a delete
 * disguised as a move. The one place a pre-start date is legitimate is where the session is ALREADY
 * there, which is the mid-week start's own withheld set, and dragging those forward is the repair.
 */
export type DropRefusal = 'sameDay' | 'beforeStart' | 'outsideWindow' | 'outsideCycle';

export type DropVerdict = { readonly ok: true } | { readonly ok: false; readonly refusal: DropRefusal };

const ALLOWED: DropVerdict = { ok: true };

export interface DropContext {
  /** `null` before the trainer picks one — and then the grid is not drawn, so nothing can be dropped. */
  readonly startDate: Date | null;
  /** The same window `assignRequest` judges an override against, so the two cannot disagree. */
  readonly window: DateWindow;
}

/**
 * The Monday-to-Sunday week this session's cycle occupies — the only days it may be moved between.
 *
 * Shared with `occurrenceEdit`, which floors and caps the popover's own picker to it, so the two
 * paths cannot come to different answers about the same session.
 */
export function cycleWindow(chip: ProjectionChip): DateWindow {
  const pattern = parseIsoDate(chip.patternIso) ?? parseIsoDate(chip.iso);
  if (pattern === null) return { earliest: new Date(0), latest: new Date(0) };
  const monday = mondayOf(pattern);
  return { earliest: monday, latest: addDays(monday, 6) };
}

/** Whether this chip may land on this ISO date. */
export function dropVerdict(
  chip: ProjectionChip,
  targetIso: string,
  { startDate, window }: DropContext,
): DropVerdict {
  const target = parseIsoDate(targetIso);
  if (target === null || startDate === null) return { ok: false, refusal: 'outsideWindow' };
  if (targetIso === chip.iso) return { ok: false, refusal: 'sameDay' };

  const day = atMidnight(target).getTime();
  if (day < window.earliest.getTime() || day > window.latest.getTime()) {
    return { ok: false, refusal: 'outsideWindow' };
  }
  if (day < atMidnight(startDate).getTime()) return { ok: false, refusal: 'beforeStart' };

  const cycle = cycleWindow(chip);
  if (day < cycle.earliest.getTime() || day > cycle.latest.getTime()) {
    return { ok: false, refusal: 'outsideCycle' };
  }

  return ALLOWED;
}

/**
 * The override map after the drop. The chip's own time rides along — a move changes the DAY, and a
 * Stationary session that was retimed in the popover keeps the hour the trainer chose for it.
 *
 * Returns the map unchanged when the drop is refused, so a caller cannot half-apply one.
 */
export function applyDrop(
  overrides: ReadonlyMap<string, OccurrenceOverride>,
  chip: ProjectionChip,
  targetIso: string,
  context: DropContext,
): ReadonlyMap<string, OccurrenceOverride> {
  if (!dropVerdict(chip, targetIso, context).ok) return overrides;

  const next = new Map(overrides);
  const key = coordinateKey(chip.coordinate);

  // Back on its own generated day: the trainer has undone the move — see the header.
  if (targetIso === chip.patternIso) {
    next.delete(key);
    return next;
  }

  const target = parseIsoDate(targetIso);
  if (target === null) return overrides;
  next.set(key, { date: target, time: next.get(key)?.time ?? null });
  return next;
}
